import { ForwardedRef, forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { useText } from '../../../utils/hooks/use_text';
import { theme } from './label.theme';

interface LabelProps {
	/**
	 * Reflects the value of the native HTML Label `for` property.
	 */
	htmlFor?: string;

	/**
	 * Use to indicate if the labeled input element is mandatory or not.
	 */
	optional?: boolean;

	/**
	 * Reflects the value of the native HTML Input `id` property.
	 */
	id?: string;
	type?: 'label' | 'legend';

	/**
	 * The content in the button.
	 */
	children: ReactNode;
}

const elements = {
	label: styled.div`
		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		font: ${theme.getValue('baseFont')};
		color: ${theme.getValue('baseTextColor')};
		word-break: break-word;
	`,
	optional: styled.span`
		font: ${theme.getValue('optionalFont')};
		color: ${theme.getValue('optionalTextColor')};
		word-break: keep-all;
	`,
};

function CellulaLabel(
	{ type = 'label', optional = false, children, ...rest }: LabelProps,
	ref: ForwardedRef<HTMLLabelElement>,
): JSX.Element | null {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (!children) {
		return null;
	}

	return (
		// biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed
		<elements.label as={type} {...rest} ref={ref}>
			<span>{children}</span>
			{optional ? (
				<>
					{' '}
					<elements.optional>({staticTexts('Component.Label.Optional')})</elements.optional>
				</>
			) : null}
		</elements.label>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to labeling input elements.
 */
const Label = forwardRef(CellulaLabel);

export type { LabelProps };
export { Label };
